// 👇 Styled version of your SalaryDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DataGrid, { Column, Summary, TotalItem, Scrolling, Export, ColumnFixing, DataGrid as DxDataGrid } from 'devextreme-react/data-grid';
import type DataGridInstance from 'devextreme/ui/data_grid';
import DateBox from 'devextreme-react/date-box';
import Button from 'devextreme-react/button';
import 'devextreme/dist/css/dx.light.css';
import './App.css';

const SalaryDashboard = () => {
  const [backendStatus, setBackendStatus] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [realmId, setRealmId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date("2025-03-01"));
  const [endDate, setEndDate] = useState<Date | null>(new Date("2025-03-31"));
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);

  const API_BASE = "https://qbo.omnivistaweb.com";
  const gridRef = useRef<DxDataGrid>(null);

  useEffect(() => {
    document.body.classList.add('scroll-lock');
  
    return () => {
      document.body.classList.remove('scroll-lock');
    };
  }, []);
  
  useEffect(() => {
    axios.get(`${API_BASE}/api/qbo-config`).then((res) => {
      setAuthCode(res.data.authorization_code || '');
      setRealmId(res.data.realm_id || '');
    }).catch(() => {
      console.error("Failed to fetch config");
    });
  }, []);

  const handlePreview = async () => {
    if (!startDate || !endDate) return alert("Please select start and end date.");
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/api/preview`, {
        params: {
          start: startDate.toISOString().split("T")[0],
          end: endDate.toISOString().split("T")[0]
        },
      });
      setData(res.data.data);
      setTotalAmount(res.data.total);
    } catch (err: any) {
      console.error("Preview error:", err);
      setError(`Error fetching preview: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(
        `${API_BASE}/api/generate`,
        {
          start: startDate?.toISOString().split("T")[0],
          end: endDate?.toISOString().split("T")[0]
        },
        { responseType: "blob" }
      );

      const disposition = res.headers['content-disposition'];
      let filename = "wps_output.SIF";
      const match = disposition?.match(/filename="?([^"]+)"?/);
      if (match?.[1]) filename = match[1];

      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating SIF file:", error);
      alert("Failed to generate SIF file.");
    }
  };

  const handleCreatePaystub = (rowData: any) => {
    const txnId = rowData["TxnId"];
    if (!txnId) return alert("Missing TxnId for this row");
    window.open(`/paystub/${txnId}`, '_blank');
  };

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-controls" style={{ background: '#fff', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div className="dashboard-row spaced">
          <div className="date-range">
            <label>Start Date:</label>
            <DateBox type="date" value={startDate || undefined} onValueChanged={(e: any) => setStartDate(e.value)} />
            <label>End Date:</label>
            <DateBox type="date" value={endDate || undefined} onValueChanged={(e: any) => setEndDate(e.value)} />
            <Button text={loading ? 'Loading...' : 'Preview Results'} type="default" onClick={handlePreview} disabled={loading || !authCode || !realmId} />
            
          </div>
          {totalAmount !== null && (
            <input
              type="text"
              disabled
              value={`Total: ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} AED`}
              className="total-amount-box"
            />
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-grid-container">
        <DataGrid
          dataSource={data}
          ref={gridRef}
          showBorders={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          height="100%"
          width="100%"
          rowAlternationEnabled={true}
          showColumnHeaders={true}
        >
          <Export enabled={true} allowExportSelectedData={false} />
          <ColumnFixing enabled={true} />

          <Column dataField="TxnId" visible={false} />

          {data.length > 0 && (
            <Column
              caption=""
              width={50}
              fixed={true}
              fixedPosition="left"
              cellRender={({ data }) => (
                <Button
                  icon="download"
                  hint="Download Paystub"
                  onClick={() => handleCreatePaystub(data)}
                  stylingMode="text"
                  type="normal"
                />
              )}
            />
          )}

          {data.length > 0 && Object.keys(data[0])
            .filter(col => col.toLowerCase() !== 'payroll expenses2')
            .sort((a, b) => {
              if (a === 'Display Name') return -1;
              if (b === 'Display Name') return 1;
              return a.localeCompare(b);
            })
            .map(col => {
              const isFrozen = ['TxnId', 'Display Name', 'First Name', 'Unique Employee ID'].includes(col);
              return (
                <Column
                  key={col}
                  dataField={col}
                  caption={col}
                  sortOrder={col === 'Display Name' ? 'asc' : undefined}    // ✅ default sort direction
                  sortIndex={col === 'Display Name' ? 0 : undefined}        // ✅ apply it as first sort
                  allowSorting={true}
                  alignment={typeof data[0][col] === 'number' ? 'right' : 'left'}
                  format={typeof data[0][col] === 'number' ? { type: 'fixedPoint', precision: 2 } : undefined}
                  fixed={isFrozen}
                  fixedPosition="left"
                />
              );
            })}


          <Scrolling mode="virtual" />
        </DataGrid>
      </div>

      {data.length > 0 && (
        <div style={{ marginTop: 20, textAlign: 'right' }}>
          <Button
            icon="export"
            text="Generate SIF File"
            type="success"
            stylingMode="contained"
            onClick={handleGenerate}
          />
        </div>
      )}
    </div>
  );
};

export default SalaryDashboard;
